import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

interface SafeMathRendererProps {
  content: string;
  inline?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onError?: (error: string) => void;
}

/**
 * 安全的数学公式渲染器 - 使用DOM渲染但避免nextSibling错误
 * 
 * 核心原理：
 * - 使用useRef和useEffect进行安全的DOM操作
 * - 在组件卸载前正确清理MathJax相关的DOM
 * - 添加渲染超时和错误处理
 * - 使用传统的MathJax DOM渲染方式确保兼容性
 */
const SafeMathRenderer: React.FC<SafeMathRendererProps> = ({
  content,
  inline = false,
  className = '',
  style = {},
  onError
}) => {
  const containerRef = useRef<HTMLDivElement | HTMLSpanElement>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!content || typeof content !== 'string') {
      return;
    }

    const renderMath = async () => {
      if (!containerRef.current || !isMountedRef.current) {
        return;
      }

      try {
        setIsRendering(true);
        setRenderError(null);

        // 检测是否包含数学公式
        const hasMath = /\$|\\\(|\\\[|\\begin\{|\\ce\{|\\mathrm\{/.test(content);
        
        if (!hasMath) {
          // 没有数学公式，直接设置HTML内容
          containerRef.current.innerHTML = content;
          setIsRendering(false);
          return;
        }

        // 首先设置HTML内容，这样即使MathJax失败，内容也能正常显示
        containerRef.current.innerHTML = content;

        // 检查MathJax是否可用
        if (typeof window === 'undefined' || !window.MathJax) {
          console.warn('MathJax不可用，显示原始HTML内容');
          setIsRendering(false);
          return;
        }

        // 等待MathJax加载完成
        await waitForMathJaxReady();

        if (!isMountedRef.current) {
          return;
        }

        // 尝试使用MathJax渲染，但如果失败也不影响HTML内容显示
        if (window.MathJax.typesetPromise && typeof window.MathJax.typesetPromise === 'function') {
          await Promise.race([
            window.MathJax.typesetPromise([containerRef.current]),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('MathJax渲染超时')), 8000)
            )
          ]);
        } else {
          console.warn('MathJax.typesetPromise不可用，仅显示HTML内容');
        }

        if (!isMountedRef.current) {
          return;
        }

        setIsRendering(false);

      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }

        const errorMsg = `数学公式渲染失败: ${error instanceof Error ? error.message : String(error)}`;
        console.warn(errorMsg);
        setRenderError(errorMsg);
        onError?.(errorMsg);
        
        // 出错时确保HTML内容仍然可见（不需要转义，因为已经设置过了）
        if (containerRef.current && !containerRef.current.innerHTML) {
          containerRef.current.innerHTML = content;
        }
        setIsRendering(false);
      }
    };

    renderMath();

    // 清理函数
    return () => {
      if (containerRef.current) {
        // 清理可能的MathJax渲染状态
        try {
          containerRef.current.innerHTML = '';
        } catch (error) {
          console.warn('清理MathJax容器失败:', error);
        }
      }
    };
  }, [content, onError]);

  return (
    <Box
      ref={containerRef}
      component={inline ? 'span' : 'div'}
      className={`safe-math-renderer ${className} ${isRendering ? 'rendering' : ''}`}
      style={{
        display: inline ? 'inline' : 'block',
        lineHeight: '1.6',
        fontSize: '14px',
        minHeight: isRendering ? '1.2em' : 'auto',
        opacity: isRendering ? 0.7 : 1,
        ...style
      }}
    />
  );
};

/**
 * 等待MathJax准备就绪 - 简化检测逻辑，更加宽松
 */
async function waitForMathJaxReady(maxWait = 10000): Promise<void> {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWait) {
    // 简化检查：只要typesetPromise方法可用就认为准备就绪
    if (window.MathJax && 
        window.MathJax.typesetPromise && 
        typeof window.MathJax.typesetPromise === 'function') {
      console.log('✅ MathJax已准备就绪');
      return;
    }
    
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  console.warn('⚠️ MathJax等待超时，但仍将尝试渲染');
  // 不抛出错误，让渲染继续进行
}

/**
 * 转义HTML字符
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export default SafeMathRenderer;